import React, { useState, useEffect, useRef } from "react";
import { Button, Row, Col, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const GuidePage = () => {
  const [guideData, setGuideData] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef(null);

  // 监听屏幕尺寸，判断是否为移动端（768px为Bootstrap默认md断点）
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchGuide();
  }, []);

  const fetchGuide = () => {
    const journal = "/json/guidelines/common.json";
    fetch(journal)
      .then((response) => response.json())
      .then((data) => {
        setGuideData(data || []);
        setExpandedItems({});
      })
      .catch((error) => console.error(`Error loading common:`, error));
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleItemClick = (id, e) => {
    e.preventDefault();
    const targetElement = document.getElementById(id);

    if (targetElement && sidebarRef.current) {
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const sidebarTop = sidebarRect.top;
      const targetRect = targetElement.getBoundingClientRect();
      const targetTop = targetRect.top;
      const scrollDistance = targetTop - sidebarTop;

      window.scrollBy({
        top: scrollDistance,
        behavior: "smooth",
      });

      if (isMobile) {
        setExpandedItems({});
      }
    }
  };

  // 递归渲染菜单
  const renderMenuItems = (items, level = 0) => {
    return items.map((item) => (
      <li key={item.id} className={`mb-2 ${level > 0 ? "ms-3" : ""}`}>
        <a
          href={`#${item.id}`}
          className="btn d-flex justify-content-between align-items-center btn-secondary"
          style={{
            textAlign: "left",
            padding: isMobile ? "6px 12px" : "8px 15px",
            borderRadius: "8px",
            transition: "all 0.3s ease",
            width: "100%",
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
          onClick={(e) => {
            // 如果有子菜单且当前点击的是展开/折叠操作，则不执行跳转
            if (e) {
              handleItemClick(item.id, e);
            }
          }}
        >
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexGrow: 1,
              marginRight: "8px",
            }}
          >
            {item.title}
          </span>

          {item.subsections && item.subsections.length > 0 && (
            <FontAwesomeIcon
              icon={expandedItems[item.id] ? faChevronDown : faChevronRight}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡到父元素
                e.preventDefault(); // 阻止默认链接行为
                toggleExpand(item.id);
              }}
            />
          )}
        </a>

        {item.subsections &&
          item.subsections.length > 0 &&
          expandedItems[item.id] && (
            <ul
              style={{
                listStyle: "none",
                padding: "5px 0 0 0",
                marginTop: "5px",
              }}
            >
              {renderMenuItems(item.subsections, level + 1)}
            </ul>
          )}
      </li>
    ));
  };

  // 递归渲染内容（保持不变）
  const renderContentItems = (items, level = 0) => {
    return items.map((item) => (
      <React.Fragment key={item.id}>
        <section
          id={item.id}
          className="card mb-4 p-4"
          style={{ marginBottom: isMobile ? "1.5rem" : "1rem" }}
        >
          <h2
            className="mb-3"
            style={{
              color: "#2c3e50",
              fontSize:
                level === 0
                  ? isMobile
                    ? "1.5rem"
                    : "1.8rem"
                  : isMobile
                  ? "1.3rem"
                  : "1.5rem",
              borderLeft: `4px solid ${level === 0 ? "#000000" : "#000000"}`,
              paddingLeft: "15px",
              marginBottom: isMobile ? "1rem" : "0.5rem",
            }}
          >
            {item.title}
          </h2>

          {item.table ? (
            <div
              className="mt-3 overflow-x-auto"
              style={{
                minWidth: "320px",
                marginBottom: isMobile ? "1rem" : "0.5rem",
              }}
            >
              <table className="table table-bordered w-100">
                <thead className="text-center bg-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Article Type
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Description
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Length
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.table.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.type}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.desc}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.length}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                lineHeight: "1.8",
                color: "#555",
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                textAlign: isMobile ? "":"justify",
                wordSpacing:"2px",
                marginBottom: isMobile ? "1rem" : "0.5rem",
              }}
              dangerouslySetInnerHTML={{
                __html: (item.content || "").replace(/\n/g, "<br>"),
              }}
            />
          )}

          {item.table_apc ? (
            <div className="mt-3 overflow-x-auto" style={{ minWidth: "320px" }}>
              <table className="table table-bordered w-100 text-center">
                <thead className="bg-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Journal Name
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Article Publishing Charge, APC
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.table_apc.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.name}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.article}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            ""
          )}

          {item.details &&
            item.details.map((detail, detailIndex) => (
              <div key={detailIndex}>
                <div 
                  style={{
                    fontSize: isMobile ? "0.95rem" : "1rem",
                    textAlign: isMobile ? "":"justify",
                    wordSpacing:"2px",
                    color: "#2c3e50",
                  }}
                >
                  {detail.title}
                </div>

                <ul
                  style={{
                    listStyleType: "disc",
                    paddingLeft: isMobile ? "20px" : "30px",
                    lineHeight: "1.8",
                    color: "#555",
                    fontSize: isMobile ? "0.95rem" : "1rem",
                  }}
                >
                  {detail.content &&
                    detail.content.map((contentItem, contentIndex) => (
                      <li
                        key={contentIndex}
                        style={{ marginBottom: isMobile ? "8px" : "10px",
                        textAlign: isMobile ? "":"justify", }}
                      >
                        {contentItem.title}
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </section>

        {item.subsections &&
          item.subsections.length > 0 &&
          renderContentItems(item.subsections, level + 1)}
      </React.Fragment>
    ));
  };

  return (
    <div>
      <div className="page-title">
        <h1>Author Guidelines</h1>
      </div>

      <Row gap={4} style={{ position: "relative" }}>
        <Col xs={12} md={3}>
          <Card
            ref={sidebarRef}
            style={{
              maxHeight: isMobile
                ? "calc(100vh - 40px)"
                : "calc(100vh - 100px)",
              overflowY: "auto",
              padding: isMobile ? "1rem" : "1.5rem",
              position: isMobile ? "static" : "sticky",
              top: isMobile ? "auto" : "100px",
              left: isMobile ? "auto" : 0,
              zIndex: 10,
            }}
          >
            <Card.Header className="tab-header">
              <h3
                style={{
                  color: "#2c3e50",
                  paddingBottom: "10px",
                  margin: 0,
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                }}
              >
                Content Overview
              </h3>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {renderMenuItems(guideData)}
              </ul>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={9}>
          <main>
            {guideData.length > 0 ? (
              renderContentItems(guideData)
            ) : (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <div className="text-center">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p>Loading content</p>
                </div>
              </div>
            )}
          </main>
        </Col>
      </Row>
    </div>
  );
};

export default GuidePage;
